/**
 * SC-209: making a second document look like Sevv.
 *
 * A picture-in-picture window is a whole separate `Document`. **Nothing travels into it** — not the
 * stylesheets, not the `:root` custom properties the Environment system writes, not the style tags emotion
 * and styled-components have already injected. Without this module the window renders as unstyled HTML,
 * which is exactly what it looks like the first time you open one.
 *
 * Three separate handoffs, because they fail in three different ways:
 *
 *  1. **The stylesheets** (`collectStyleRules` → `applyStyles`). Read the rules we are allowed to read and
 *     copy them verbatim; for the sheets a cross-origin policy hides from us, pass the URL and let the other
 *     document fetch it itself.
 *  2. **The theme** (`copySevvVars`, `watchSevvVars`). The copied rules are full of `var(--sevv-*)`, and every
 *     one of those variables lives in an inline style on the opener's `<html>`, written by
 *     `applySevvCssVariables`. Copying the rules without the variables gives you every fallback colour and
 *     none of the workspace's own.
 *  3. **Styles written from here on** (`createPipEmotionCache`). Anything React renders into the window after
 *     it opens generates new emotion rules, and they must be inserted into *its* head, not ours.
 *
 * Call order matters and the renderer owns it: `applyStyles` first, then React. Emotion appends its tags to
 * the head it is given, so the copied application rules sit above them and component styles win ties, which
 * is the same precedence they have in the main document.
 *
 * (Minor shape deviation from plan §1 task 1.4: `applyStyles` takes the `CollectedStyles` object that
 * `collectStyleRules` returns rather than its two fields as separate arguments — two same-shaped arguments
 * in a row is a swap waiting to happen.)
 */

import createCache from '@emotion/cache'
import type { EmotionCache } from '@emotion/cache'

/** Every theme variable `applySevvCssVariables` writes shares this prefix; nothing else on the root does. */
const SEVV_VAR_PREFIX = '--sevv-'

/** The resolved light/dark stamp that goes with those variables — see `applySevvCssVariables`. */
const THEME_MODE_ATTRIBUTE = 'data-sevv-theme'

export interface CollectedStyles {
  /** Every rule we were allowed to read, already serialised and ready to drop into a `<style>`. */
  rules: string
  /** Sheets whose contents a cross-origin policy hid from us; the other document has to load these itself. */
  hrefs: string[]
}

/**
 * Serialises a document's stylesheets so another document can wear them.
 *
 * A sheet loaded from another origin throws on `cssRules` rather than returning an empty list, so the throw
 * is the detection: it means "you may not read this", and the only thing left to pass on is the URL.
 */
export function collectStyleRules(doc: Document): CollectedStyles {
  // `@import` is only honoured at the very top of a stylesheet, so hoisting them keeps the fonts that Vite
  // leaves as imports during development. Everything else keeps its original order and therefore its cascade.
  const imports: string[] = []
  const rules: string[] = []
  const hrefs: string[] = []

  for (const sheet of Array.from(doc.styleSheets)) {
    let sheetRules: CSSRuleList | null = null
    try {
      sheetRules = sheet.cssRules
    } catch {
      if (sheet.href) hrefs.push(sheet.href)
      continue
    }

    if (!sheetRules) {
      if (sheet.href) hrefs.push(sheet.href)
      continue
    }

    for (const rule of Array.from(sheetRules)) {
      const text = rule.cssText
      if (text.trimStart().startsWith('@import')) imports.push(text)
      else rules.push(text)
    }
  }

  return { rules: [...imports, ...rules].join('\n'), hrefs }
}

/**
 * Installs collected styles into another document's head.
 *
 * The links go in first so that the rules we copied — the application's own — override anything a webfont or
 * third-party sheet declares, which is the precedence they had in the document they came from.
 */
export function applyStyles(pipDoc: Document, styles: CollectedStyles): void {
  for (const href of styles.hrefs) {
    const link = pipDoc.createElement('link')
    link.rel = 'stylesheet'
    link.href = href
    pipDoc.head.appendChild(link)
  }

  if (!styles.rules) return

  const style = pipDoc.createElement('style')
  style.setAttribute('data-sevv-pip', 'copied-rules')
  style.textContent = styles.rules
  pipDoc.head.appendChild(style)
}

/**
 * Mirrors the workspace theme from one root element onto another.
 *
 * Variables that have gone away are cleared first: an admin who removes a colour would otherwise leave the
 * floating window showing it long after the tab behind it had moved on. The light/dark stamp travels with
 * them because it is the other half of the same decision — the variables say which colours, the attribute
 * says which mode resolved them.
 */
export function copySevvVars(fromRoot: HTMLElement, toRoot: HTMLElement): void {
  // Backwards, because removing a property renumbers everything after it.
  for (let i = toRoot.style.length - 1; i >= 0; i -= 1) {
    const name = toRoot.style.item(i)
    if (name.startsWith(SEVV_VAR_PREFIX)) toRoot.style.removeProperty(name)
  }

  for (let i = 0; i < fromRoot.style.length; i += 1) {
    const name = fromRoot.style.item(i)
    if (!name.startsWith(SEVV_VAR_PREFIX)) continue
    toRoot.style.setProperty(name, fromRoot.style.getPropertyValue(name))
  }

  const mode = fromRoot.getAttribute(THEME_MODE_ATTRIBUTE)
  if (mode === null) toRoot.removeAttribute(THEME_MODE_ATTRIBUTE)
  else toRoot.setAttribute(THEME_MODE_ATTRIBUTE, mode)
}

/**
 * Keeps the mirror true for as long as the window is open, and copies once immediately so a caller cannot
 * open a window that is briefly unthemed.
 *
 * An admin changing the workspace colours re-runs `applySevvCssVariables` against the opener's root, and the
 * floating window has no other way to hear about it. Returns the unsubscribe; the renderer calls it on close.
 */
export function watchSevvVars(fromRoot: HTMLElement, toRoot: HTMLElement): () => void {
  copySevvVars(fromRoot, toRoot)

  const observer = new MutationObserver(() => copySevvVars(fromRoot, toRoot))
  observer.observe(fromRoot, { attributes: true, attributeFilter: ['style', THEME_MODE_ATTRIBUTE] })

  return () => observer.disconnect()
}

/**
 * The emotion cache for everything React renders inside the window.
 *
 * MUI and our styled wrappers are emotion, and emotion inserts into whichever head its cache points at — by
 * default the opener's, where the floating window cannot see it. A distinct `key` matters as much as the
 * container: it keeps these class names from colliding with the `css-*` ones already copied over, so the two
 * documents cannot fight over a hash.
 */
export function createPipEmotionCache(pipDoc: Document): EmotionCache {
  return createCache({ key: 'pip', container: pipDoc.head })
}
